import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ScoreEntry.css';
import bishopGamesLogo from '../img/bishop_games_logo.png';

const ScoreEntry = () => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSport, setSelectedSport] = useState('');
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [scoreData, setScoreData] = useState({
    team1Score: '',
    team2Score: '',
    winnerTeamId: '',
    draw: false,
    bonusPoints: 0,
    sportMatchPoints: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const TOURNAMENT_ID = '215f8af9-c786-4b42-8f14-adae8bef13f4';

  // Sports data
  const sports = [
    { id: '5F68C7E0-DA61-4142-94E6-B3F018C5255C', code: 'BADMINTON', name: 'Badminton', scoringType: 'SET' },
    { id: '524FB7B9-D5CF-4291-B878-3A38F57C36FA', code: 'BOX_CRICKET', name: 'Box Cricket', scoringType: 'POINT' },
    { id: 'C22D70AC-F8BD-4916-924F-5D4E479AFA78', code: 'CARROM', name: 'Carrom', scoringType: 'SET' },
    { id: '490509BA-0AE1-46D3-8359-2194BDE803BC', code: 'CHESS', name: 'Chess', scoringType: 'RESULT' },
    { id: 'F0F86724-6315-4918-9C4F-4F4B8C7F230E', code: 'CROQUET', name: 'Croquet', scoringType: 'POINT' },
    { id: 'B382B4E3-004A-49B9-A6A5-9AA3CF1B2447', code: 'FUTSAL', name: 'Futsal', scoringType: 'POINT' },
    { id: '6DB11D9F-C504-4CB6-8A2A-B7B8C66EED25', code: 'SATODIYU', name: 'Satodiyu', scoringType: 'POINT' },
    { id: '7CD6C673-1965-486D-A12F-6FB286648373', code: 'TABLE_TENNIS', name: 'Table Tennis', scoringType: 'SET' },
    { id: 'B5C67FD9-80B3-4FBF-947D-541BDE90A8F7', code: 'TENNIS', name: 'Tennis', scoringType: 'SET' },
    { id: '97B9519C-D408-42C9-BA13-1E2DB680758B', code: 'VOLLEYBALL', name: 'Volleyball', scoringType: 'SET' }
  ];

  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem('token') || localStorage.getItem('Token');
  };

  // Check user role and authorization
  useEffect(() => {
    checkAuthorization();
  }, []);

  const checkAuthorization = () => {
    const token = getAuthToken();
    if (!token) {
      setErrorMessage('Please login to access this page');
      setLoading(false);
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    // Decode token to get user role (adjust based on your token structure)
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        const role = payload.role || payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
        
        console.log('User role:', role);
        
        if (role === 'ADMIN' || role === 'UMPR') {
          setUserRole(role);
          fetchTeams(token);
        } else {
          setErrorMessage('Access Denied: Only Admins and Umpires can access this page');
          setTimeout(() => navigate('/'), 3000);
        }
      }
    } catch (error) {
      console.error('Error checking authorization:', error);
      setErrorMessage('Authorization error. Please login again.');
      setTimeout(() => navigate('/login'), 2000);
    }
    setLoading(false);
  };

  const fetchTeams = async (token) => {
    try {
      const response = await fetch('https://api.asterickx.com/api/Teams', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchUncompletedMatches = async (sportId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(
        `https://api.asterickx.com/api/standby-tournament/schedule/${TOURNAMENT_ID}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const allMatches = await response.json();
        console.log('All matches:', allMatches);
        
        // Filter by sport and status (not completed)
        const filtered = allMatches.filter(match => 
          match.sportId.toLowerCase() === sportId.toLowerCase() && 
          match.status !== 'Completed'
        );
        
        console.log('Filtered uncompleted matches:', filtered);
        setUpcomingMatches(filtered);
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
      setErrorMessage('Failed to load matches');
    }
  };

  const handleSportChange = (sportId) => {
    setSelectedSport(sportId);
    setSelectedMatch(null);
    setUpcomingMatches([]);
    setSuccessMessage('');
    setErrorMessage('');
    
    if (sportId) {
      fetchUncompletedMatches(sportId);
    }
  };

  const handleMatchSelect = (match) => {
    setSelectedMatch(match);
    setScoreData({
      team1Score: '',
      team2Score: '',
      winnerTeamId: '',
      draw: false,
      bonusPoints: 0,
      sportMatchPoints: 0
    });
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleScoreChange = (field, value) => {
    setScoreData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-determine winner when scores change
    if (field === 'team1Score' || field === 'team2Score') {
      const team1 = field === 'team1Score' ? parseInt(value) : parseInt(scoreData.team1Score);
      const team2 = field === 'team2Score' ? parseInt(value) : parseInt(scoreData.team2Score);
      
      if (!isNaN(team1) && !isNaN(team2)) {
        if (team1 > team2) {
          setScoreData(prev => ({ ...prev, winnerTeamId: selectedMatch.team1Id, draw: false }));
        } else if (team2 > team1) {
          setScoreData(prev => ({ ...prev, winnerTeamId: selectedMatch.team2Id, draw: false }));
        } else if (team1 === team2 && team1 > 0) {
          setScoreData(prev => ({ ...prev, winnerTeamId: '', draw: true }));
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!scoreData.team1Score || !scoreData.team2Score) {
      setErrorMessage('Please enter scores for both teams');
      return;
    }

    if (!scoreData.draw && !scoreData.winnerTeamId) {
      setErrorMessage('Please select a winner or mark as draw');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    const token = getAuthToken();
    const payload = {
      scheduleId: selectedMatch.id,
      team1Score: parseInt(scoreData.team1Score),
      team2Score: parseInt(scoreData.team2Score),
      winnerTeamId: scoreData.draw ? null : scoreData.winnerTeamId,
      draw: scoreData.draw,
      bonusPoints: parseInt(scoreData.bonusPoints) || 0,
      sportMatchPoints: parseInt(scoreData.sportMatchPoints) || 0
    };

    console.log('Submitting score:', payload);

    try {
      const response = await fetch(
        'https://api.asterickx.com/api/standby-tournament/score',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log('Score submitted successfully:', result);
        
        const team1Name = getTeamName(selectedMatch.team1Id);
        const team2Name = getTeamName(selectedMatch.team2Id);
        const winnerName = scoreData.draw ? 'Draw' : getTeamName(scoreData.winnerTeamId);
        
        setSuccessMessage(`Score submitted! ${team1Name} ${scoreData.team1Score} - ${scoreData.team2Score} ${team2Name}. Winner: ${winnerName}`);
        
        // Refresh matches list to remove completed match
        setTimeout(() => {
          fetchUncompletedMatches(selectedSport);
          setSelectedMatch(null);
          setScoreData({
            team1Score: '',
            team2Score: '',
            winnerTeamId: '',
            draw: false,
            bonusPoints: 0,
            sportMatchPoints: 0
          });
          setSuccessMessage('');
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => null);
        console.error('Error response:', errorData);
        setErrorMessage(errorData?.message || `Failed to submit score (Status: ${response.status})`);
      }
    } catch (error) {
      console.error('Error submitting score:', error);
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTeamName = (teamId) => {
    if (!teamId) return 'Unknown';
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : 'Unknown Team';
  };

  const getSportName = (sportId) => {
    const sport = sports.find(s => s.id.toLowerCase() === sportId.toLowerCase());
    return sport ? sport.name : 'Unknown Sport';
  };

  const formatDateTime = (match) => {
    if (!match.matchDate || !match.matchTime) return { date: 'N/A', time: 'N/A' };
    
    const dateObj = new Date(match.matchDate);
    const dateOptions = { weekday: 'short', month: 'short', day: 'numeric' };
    const formattedDate = dateObj.toLocaleDateString('en-US', dateOptions);
    
    // Convert time
    const timeParts = match.matchTime.split(':');
    let hours = parseInt(timeParts[0]);
    const minutes = timeParts[1];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const formattedTime = `${hours}:${minutes} ${ampm}`;
    
    return { date: formattedDate, time: formattedTime };
  };

  if (loading) {
    return (
      <div className="score-entry-container">
        <div className="loading-container">
          <div className="spinner-border"></div>
          <p>Checking authorization...</p>
        </div>
      </div>
    );
  }

  if (!userRole) {
    return (
      <div className="score-entry-container">
        <div className="error-container">
          <i className="fas fa-exclamation-circle"></i>
          <p>{errorMessage || 'Access Denied'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="score-entry-container">
      {/* Header */}
      <div className="score-entry-header">
        <button className="back-btn-score" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left me-2"></i>
          Back
        </button>
        
        <div className="header-content-score">
          <div className="header-logo-score">
            <img src={bishopGamesLogo} alt="Bishop Games" className="logo-score" />
          </div>
          <div className="header-text-score">
            <h1 className="score-entry-title">
              <i className="fas fa-edit me-2"></i>
              Score Entry
            </h1>
            <p className="score-entry-subtitle">Bishop Games 6.0 - {userRole === 'ADMIN' ? 'Admin' : 'Umpire'} Panel</p>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="alert-message success-alert-score">
          <i className="fas fa-check-circle me-2"></i>
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="alert-message error-alert-score">
          <i className="fas fa-exclamation-circle me-2"></i>
          {errorMessage}
        </div>
      )}

      {/* Sport Selection */}
      <div className="sport-selection-section">
        <h2 className="section-title-score">
          <i className="fas fa-futbol me-2"></i>
          SELECT SPORT
        </h2>
        <div className="sports-grid">
          {sports.map(sport => (
            <button
              key={sport.id}
              className={`sport-card ${selectedSport === sport.id ? 'selected' : ''}`}
              onClick={() => handleSportChange(sport.id)}
            >
              <span className="sport-name">{sport.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Match Selection */}
      {selectedSport && (
        <div className="match-selection-section">
          <h2 className="section-title-score">
            <i className="fas fa-calendar-alt me-2"></i>
            SELECT MATCH ({upcomingMatches.length} UNCOMPLETED)
          </h2>
          
          {upcomingMatches.length > 0 ? (
            <div className="matches-list">
              {upcomingMatches.map(match => {
                const { date, time } = formatDateTime(match);
                return (
                  <div
                    key={match.id}
                    className={`match-card-select ${selectedMatch?.id === match.id ? 'selected' : ''}`}
                    onClick={() => handleMatchSelect(match)}
                  >
                    <div className="match-teams-select">
                      <span className="team-name-select">{getTeamName(match.team1Id)}</span>
                      <span className="vs-select">VS</span>
                      <span className="team-name-select">{getTeamName(match.team2Id)}</span>
                    </div>
                    <div className="match-details-select">
                      <span><i className="fas fa-calendar me-1"></i>{date}</span>
                      <span><i className="fas fa-clock me-1"></i>{time}</span>
                      <span><i className="fas fa-map-marker-alt me-1"></i>{match.courtNumber || 'TBA'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-matches">
              <i className="fas fa-check-double"></i>
              <p>All matches completed for this sport!</p>
            </div>
          )}
        </div>
      )}

      {/* Score Entry Form */}
      {selectedMatch && (
        <div className="score-form-section">
          <h2 className="section-title-score">
            <i className="fas fa-pencil-alt me-2"></i>
            ENTER SCORE
          </h2>
          
          <form onSubmit={handleSubmit} className="score-form">
            {/* Match Info */}
            <div className="match-info-display">
              <div className="info-row">
                <span className="info-label">Sport:</span>
                <span className="info-value">{getSportName(selectedMatch.sportId)}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Round:</span>
                <span className="info-value">{selectedMatch.roundName || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Court:</span>
                <span className="info-value">{selectedMatch.courtNumber || 'TBA'}</span>
              </div>
            </div>

            {/* Score Inputs */}
            <div className="score-input-grid">
              <div className="team-score-section">
                <label className="team-score-label">{getTeamName(selectedMatch.team1Id)}</label>
                <input
                  type="number"
                  min="0"
                  value={scoreData.team1Score}
                  onChange={(e) => handleScoreChange('team1Score', e.target.value)}
                  className="score-input-large"
                  placeholder="0"
                  required
                />
              </div>

              <div className="vs-divider-score">VS</div>

              <div className="team-score-section">
                <label className="team-score-label">{getTeamName(selectedMatch.team2Id)}</label>
                <input
                  type="number"
                  min="0"
                  value={scoreData.team2Score}
                  onChange={(e) => handleScoreChange('team2Score', e.target.value)}
                  className="score-input-large"
                  placeholder="0"
                  required
                />
              </div>
            </div>

            {/* Winner Selection */}
            <div className="winner-section">
              <label className="winner-label">Winner:</label>
              <div className="winner-buttons">
                <button
                  type="button"
                  className={`winner-btn ${scoreData.winnerTeamId === selectedMatch.team1Id ? 'selected' : ''}`}
                  onClick={() => setScoreData(prev => ({ ...prev, winnerTeamId: selectedMatch.team1Id, draw: false }))}
                >
                  {getTeamName(selectedMatch.team1Id)}
                </button>
                <button
                  type="button"
                  className={`winner-btn draw-btn ${scoreData.draw ? 'selected' : ''}`}
                  onClick={() => setScoreData(prev => ({ ...prev, winnerTeamId: '', draw: true }))}
                >
                  Draw
                </button>
                <button
                  type="button"
                  className={`winner-btn ${scoreData.winnerTeamId === selectedMatch.team2Id ? 'selected' : ''}`}
                  onClick={() => setScoreData(prev => ({ ...prev, winnerTeamId: selectedMatch.team2Id, draw: false }))}
                >
                  {getTeamName(selectedMatch.team2Id)}
                </button>
              </div>
            </div>

            {/* Additional Fields */}
            <div className="additional-fields">
              <div className="field-group">
                <label>Bonus Points:</label>
                <input
                  type="number"
                  min="0"
                  value={scoreData.bonusPoints}
                  onChange={(e) => handleScoreChange('bonusPoints', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="field-group">
                <label>Sport Match Points:</label>
                <input
                  type="number"
                  min="0"
                  value={scoreData.sportMatchPoints}
                  onChange={(e) => handleScoreChange('sportMatchPoints', e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="form-actions-score">
              <button
                type="button"
                className="btn-cancel-score"
                onClick={() => {
                  setSelectedMatch(null);
                  setScoreData({
                    team1Score: '',
                    team2Score: '',
                    winnerTeamId: '',
                    draw: false,
                    bonusPoints: 0,
                    sportMatchPoints: 0
                  });
                }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-submit-score"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-2"></i>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    Submit Score
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ScoreEntry;